import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface Review {
  reviewId: string;
  bookingId: string;
  providerId: string;
  customerId: string;
  rating: number;
  comment?: string;
  approved?: boolean;
  createdAt: string;
}

export interface ReviewListResponse {
  reviews: Review[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListReviewsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  providerId?: string;
  customerId?: string;
  rating?: number;
  approved?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface ModerateReviewRequest {
  action: 'approve' | 'reject' | 'delete';
}

class ReviewsManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private async getAuthHeaders() {
    const token = adminAuthService.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async listReviews(params?: ListReviewsParams): Promise<ReviewListResponse> {
    const headers = await this.getAuthHeaders();
    const queryParams = new URLSearchParams();
    
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.providerId) queryParams.append('providerId', params.providerId);
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    if (params?.rating) queryParams.append('rating', params.rating.toString());
    if (params?.approved !== undefined) queryParams.append('approved', params.approved.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await this.api.get<{data: ReviewListResponse}>(
      `/admin/reviews?${queryParams.toString()}`,
      { headers }
    );
    return response.data.data;
  }

  async getReview(reviewId: string): Promise<Review> {
    const headers = await this.getAuthHeaders();
    const response = await this.api.get<{data: {review: Review}}>(
      `/admin/reviews/${reviewId}`,
      { headers }
    );
    return response.data.data.review;
  }

  async moderateReview(reviewId: string, action: 'approve' | 'reject' | 'delete'): Promise<Review | {message: string}> {
    const headers = await this.getAuthHeaders();
    const response = await this.api.put<{data: {review?: Review; message?: string}}>(
      `/admin/reviews/${reviewId}`,
      { action },
      { headers }
    );
    const data = response.data.data;
    if (data.review) return data.review;
    return { message: data.message ?? 'Unknown error' };
  }
}

export const reviewsManagementService = new ReviewsManagementService();
